/** Desktop dropdowns share one state for pointer, keyboard and ARIA. */
class MegaMenuController {
  constructor(nav) {
    this.nav = nav;
    this.items = [...nav.querySelectorAll('.header__nav-item--has-dropdown')];
    this.activeItem = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.openDelay = 350;
    this.closeDelay = 260;
  }

  connect() {
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => this.scheduleOpen(item), options);
      item.addEventListener('mouseleave', () => {
        this.clearOpenTimer();
        this.scheduleClose();
      }, options);
      const panel = item.querySelector('.mega-menu');
      panel?.addEventListener('mouseenter', () => {
        this.clearTimers();
        this.setOpenItem(item);
      }, options);
      panel?.addEventListener('mouseleave', () => this.scheduleClose(), options);
    });
    this.onResize = () => this.positionPanel(this.activeItem);
    window.addEventListener('resize', this.onResize, options);
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.nav.closest('.header'));
    this.nav.addEventListener('focusin', (event) => {
      this.setOpenItem(event.target.closest('.header__nav-item--has-dropdown'));
    }, options);
    this.nav.addEventListener('focusout', (event) => {
      if (!this.nav.contains(event.relatedTarget)) this.setOpenItem(null);
    }, options);
    this.nav.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activeItem) {
        event.preventDefault();
        event.stopPropagation();
        const trigger = this.activeItem.querySelector('.header__menu-item');
        // Focus first: the resulting focusin must not reopen the dismissed menu.
        trigger.focus();
        this.setOpenItem(null);
      } else if (event.key === 'ArrowDown' && event.target.matches('.header__menu-item[aria-controls]')) {
        event.preventDefault();
        const item = event.target.closest('.header__nav-item');
        this.setOpenItem(item);
        item.querySelector('.mega-menu a')?.focus();
      }
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.nav.contains(event.target)) this.setOpenItem(null);
    }, options);
  }

  disconnect() {
    this.events?.abort();
    this.resizeObserver?.disconnect();
    this.setOpenItem(null);
  }

  clearOpenTimer() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
  }

  clearTimers() {
    this.clearOpenTimer();
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  scheduleOpen(item) {
    this.clearTimers();
    if (this.activeItem === item) return;
    this.openTimer = window.setTimeout(() => this.setOpenItem(item), this.openDelay);
  }

  scheduleClose() {
    this.clearTimers();
    this.closeTimer = window.setTimeout(() => {
      // Keyboard users keep their panel while focus is inside the navigation.
      if (this.activeItem?.contains(document.activeElement)) return;
      this.setOpenItem(null);
    }, this.closeDelay);
  }

  positionPanel(item) {
    if (!item) return;
    const trigger = item.querySelector('.header__menu-item');
    const panel = item.querySelector('.mega-menu');
    const parent = panel?.offsetParent;
    if (!trigger || !parent) return;
    const top = trigger.getBoundingClientRect().bottom - parent.getBoundingClientRect().top
      + parent.scrollTop - parent.clientTop;
    panel.style.setProperty('--mega-menu-top', `${top}px`);
  }

  setOpenItem(item) {
    this.clearTimers();
    this.items.forEach((navItem) => {
      const isOpen = navItem === item;
      navItem.classList.toggle('mega-menu-open', isOpen);
      navItem.querySelector('.header__menu-item[aria-controls]')?.setAttribute('aria-expanded', String(isOpen));
    });
    this.activeItem = item;
    this.positionPanel(item);
  }
}

class MegaMenuNav extends HTMLElement {
  connectedCallback() {
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
    this.controller = null;

    this.onMediaChange = () => this.sync();
    this.mediaQuery.addEventListener('change', this.onMediaChange);
    this.sync();
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    this.controller?.disconnect();
    this.controller = null;
  }

  sync() {
    if (this.mediaQuery.matches) {
      if (!this.controller) {
        this.controller = new MegaMenuController(this);
        this.controller.connect();
      }
      return;
    }

    this.controller?.disconnect();
    this.controller = null;
  }
}

customElements.define('mega-menu-nav', MegaMenuNav);
